import { useState, useEffect } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "About", href: "#about", id: "about" },
  { label: "What We Do", href: "#what-we-do", id: "what-we-do" },
  { label: "Property Owners", href: "#owners", id: "owners" },
  { label: "Development", href: "#development", id: "development" },
  { label: "FAQ", href: "#faq", id: "faq" },
];

export const LOGO_URL = null; // set to the uploaded logo path (e.g. "/logo.svg") to replace the text wordmark instantly

export const Wordmark = ({ light = false }) =>
  LOGO_URL ? (
    <img src={LOGO_URL} alt="Choose Me Properties" className="h-8 sm:h-10 w-auto" data-testid="brand-logo-image" />
  ) : (
  <span className="leading-none">
    <span className={`font-display text-xl sm:text-2xl ${light ? "text-[#F4F1EA]" : "text-[#111513]"}`}>
      Choose Me Properties
    </span>
    <span className="font-display text-xl sm:text-2xl text-[#A77A50]">.</span>
    <span
      className={`block text-[9px] font-semibold tracking-[0.34em] uppercase mt-1.5 ${
        light ? "text-[#9AA49C]" : "text-[#718477]"
      }`}
    >
      Washington Real Estate
    </span>
  </span>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          solid
            ? "bg-[#F4F1EA]/90 backdrop-blur-md border-b border-[#D9D6CE]/70"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="#top" data-testid="nav-brand-wordmark" aria-label="Choose Me Properties — back to top">
              <Wordmark light={!solid} />
            </a>
            <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`nav-link-${l.id}`}
                  className={`text-[13px] font-semibold tracking-[0.08em] uppercase transition-colors ${
                    solid ? "text-[#3E4742] hover:text-[#111513]" : "text-[#F4F1EA]/85 hover:text-white"
                  }`}
                >
                  {l.label}
                </a>
              ))}
            </nav>
            <div className="hidden lg:block">
              <a
                href="#contact"
                data-testid="nav-lets-talk-button"
                className={`group inline-flex items-center gap-2 text-[13px] font-semibold tracking-[0.08em] uppercase px-6 py-3 transition-colors ${
                  solid
                    ? "bg-[#111513] text-[#F4F1EA] hover:bg-[#18352C]"
                    : "border border-[#F4F1EA]/40 text-[#F4F1EA] hover:border-[#A77A50] hover:text-[#A77A50]"
                }`}
              >
                Discuss a Property
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
              </a>
            </div>
            <button
              type="button"
              data-testid="nav-mobile-menu-button"
              className={`lg:hidden p-2 -mr-2 ${solid ? "text-[#111513]" : "text-[#F4F1EA]"}`}
              aria-expanded={open}
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen(!open)}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>
      {open && (
        <div className="fixed inset-0 z-40 bg-[#111513] lg:hidden overflow-y-auto" data-testid="nav-mobile-menu">
          <nav className="px-6 pt-32 pb-10" aria-label="Mobile">
            {LINKS.map((l, i) => (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => setOpen(false)}
                className="block py-4 font-display text-4xl text-[#F4F1EA] border-b border-white/10"
              >
                <span className="text-sm text-[#A77A50] font-sans font-semibold mr-4 align-middle">0{i + 1}</span>
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-mobile-lets-talk-button"
              onClick={() => setOpen(false)}
              className="mt-10 flex items-center justify-center gap-2 bg-[#A77A50] text-[#111513] text-sm font-semibold tracking-[0.1em] uppercase px-6 py-4"
            >
              Discuss a Property
              <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
            </a>
            <a
              href="tel:+12064831705"
              data-testid="nav-mobile-phone-button"
              className="mt-3 flex items-center justify-center gap-2 border border-white/20 text-[#F4F1EA] text-sm font-semibold tracking-[0.1em] uppercase px-6 py-4"
            >
              (206) 483-1705
            </a>
          </nav>
        </div>
      )}
    </>
  );
};
